import { taskDisplayTitle } from "./task-display-title";
// PHASE_4 / B1-frontend — task detail panel (description / source / due).
//
// A read-only DetailDrawer over one task: the full description, the source
// (source_spec + source_entity_kind/id), due/snooze/completed timestamps, and
// the deep-link "go to source" affordance (the task's `link` into the owning
// surface, e.g. /leave/requests/:id). The lifecycle actions live in the row +
// the footer here so the assignee can act without leaving the detail. No money.

import { useTranslation } from "react-i18next";

import { Badge, Button, DetailDrawer } from "@novemai/components";
import { formatDate, formatDateTime } from "@novemai/i18n-sdk";

import { useTimeZone } from "@/api/use-time-zone";

import type { TaskRow } from "./api";
import { classifyTaskLink } from "./task-link";
import { TaskSourceLink } from "./task-source-link";

function priorityTone(priority: string): "danger" | "warning" | "info" | "neutral" {
  switch (priority) {
    case "urgent":
      return "danger";
    case "high":
      return "warning";
    case "normal":
      return "info";
    default:
      return "neutral";
  }
}

export interface TaskDetailProps {
  readonly task: TaskRow;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Footer actions (complete/dismiss/snooze) — supplied by the host Content. */
  readonly footer?: React.ReactNode;
}

export function TaskDetail({ task, open, onClose, footer }: TaskDetailProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const none = t("tasks.detail_none");
  const instant = (iso: string): string => formatDateTime(i18n.language, iso, timeZone);

  // Both vocabularies are open (each source spec writes its own), so an
  // unmapped token reads "Other source" rather than leaking the raw id.
  const other = t("tasks.source.other");
  const specLabel = t(`tasks.source.spec.${task.source_spec}`, { defaultValue: other });
  const sourceLabel =
    task.source_entity_kind !== null
      ? t("tasks.source.pair", {
          spec: specLabel,
          entity: t(`tasks.source.entity.${task.source_entity_kind}`, { defaultValue: other }),
        })
      : specLabel;

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={t("tasks.detail_title")}
      closeLabel={t("tasks.detail_close")}
      footer={footer}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{t(`tasks.status.${task.status}`)}</Badge>
          <Badge tone={priorityTone(task.priority)}>{t(`tasks.priority.${task.priority}`)}</Badge>
        </div>

        <h3 className="text-base font-semibold">{taskDisplayTitle(task, t)}</h3>

        <section>
          <h4 className="mb-1 text-sm font-medium text-[var(--color-muted-foreground)]">
            {t("tasks.detail_description")}
          </h4>
          <p className="text-sm">
            {task.description !== null && task.description !== ""
              ? task.description
              : t("tasks.detail_no_description")}
          </p>
        </section>

        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-[var(--color-muted-foreground)]">{t("tasks.detail_source")}</dt>
          <dd>{sourceLabel}</dd>
          <dt className="text-[var(--color-muted-foreground)]">{t("tasks.detail_due")}</dt>
          <dd>{task.due_at !== null ? formatDate(i18n.language, task.due_at) : none}</dd>
          {task.snoozed_until !== null && (
            <>
              <dt className="text-[var(--color-muted-foreground)]">
                {t("tasks.detail_snoozed_until")}
              </dt>
              <dd>{instant(task.snoozed_until)}</dd>
            </>
          )}
          {task.completed_at !== null && (
            <>
              <dt className="text-[var(--color-muted-foreground)]">
                {t("tasks.detail_completed_at")}
              </dt>
              <dd>{instant(task.completed_at)}</dd>
            </>
          )}
          <dt className="text-[var(--color-muted-foreground)]">{t("tasks.detail_created")}</dt>
          <dd>{task.created_at !== null ? instant(task.created_at) : none}</dd>
        </dl>

        {task.link !== null && task.link !== "" && (
          // An unsafe href is rendered as prose, NOT wrapped in Button asChild —
          // a withheld link must not wear an actionable button's chrome.
          <div>
            {classifyTaskLink(task.link) === "unsafe" ? (
              <TaskSourceLink
                link={task.link}
                className="text-sm text-[var(--color-muted-foreground)]"
              />
            ) : (
              <Button variant="secondary" size="sm" asChild>
                <TaskSourceLink link={task.link} />
              </Button>
            )}
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}
